/**
 * The timeline (PRD 10.2; Phase 26): the run's dated work as bars over the window's weeks, with
 * the capacity lane above them.
 *
 * Every other Phase 1 view answers a question about a week — how loaded is it, by how many
 * hours, in what shape. This one answers *when*: which projects and deals occupy which weeks,
 * which of them start where the plan said they would and which have been pushed, and what the
 * company's allocation is in each of those weeks. The lane is the bridge between the two
 * readings: it is the heatmap's own row, drawn above the bars that make it up.
 *
 * The encoding is PRD Appendix A's, undiluted: the **fill** is the confidence tier (solid,
 * hatch, dots — the same three patterns the ribbon stacks), the **border** is the project type
 * hue (the assignment from `palette.js`, fixed by volume across the whole portfolio), and the
 * **border style** is the record status (solid for committed work, dashed for an opportunity).
 * Three channels, three questions, none of them colour alone.
 *
 * Phase 27 adds a fourth mark that is not an encoding channel at all: with a compare run
 * selected, each bar's position in *that* run is drawn as a dashed outline — the ghost — and the
 * movement between the two is labelled in weeks. It is deliberately achromatic and unfilled, so
 * it cannot be mistaken for a bar of its own.
 *
 * Shared by the browser client, the read model (the sort order is defined here so the API's
 * page and the card's order cannot disagree) and the tests. Pure: no DOM, no I/O.
 */
import { RECORD_STATUSES, typeSlot } from './palette.js';
import { col } from './table.js';
import { fillForTier } from './patterns.js';
import { round2 } from './heatmap.js';

/** The three kinds of bar a run produces. `recurring` is a retainer's baseline, not a schedule. */
export const TIMELINE_KINDS = Object.freeze(['project', 'recurring', 'opportunity']);

/** The orders the card offers. `start` is the default: a timeline is read down the calendar. */
export const TIMELINE_SORTS = Object.freeze(['start', 'end', 'hours', 'name']);

/** Row geometry, in pixels. The names column and the plot share them, or the rows stop lining up. */
export const ROW = Object.freeze({ height: 26, bar: 14 });

/** What each kind is called, and the record status its border takes (PRD Appendix A). */
export const KIND_VOCABULARY = Object.freeze([
  Object.freeze({
    key: 'project',
    label: 'Project',
    plural: 'Projects',
    status: 'open',
    hint: 'Committed work with a schedule, or one inferred for it',
  }),
  Object.freeze({
    key: 'recurring',
    label: 'Retainer',
    plural: 'Retainers',
    status: 'open',
    hint: 'A recurring project, forecast from its trailing baseline rather than a schedule',
  }),
  Object.freeze({
    key: 'opportunity',
    label: 'Opportunity',
    plural: 'Opportunities',
    status: 'opportunity',
    hint: 'An open deal, from its kickoff week',
  }),
]);

const KIND_BY_KEY = new Map(KIND_VOCABULARY.map((k) => [k.key, k]));

/** The record status a kind wears, and the border treatment that goes with it. */
export function statusOf(kind) {
  const status = KIND_BY_KEY.get(kind)?.status ?? 'open';
  return RECORD_STATUSES.find((s) => s.key === status) ?? RECORD_STATUSES[0];
}

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

// ---------------------------------------------------------------------------------------------
// Order

/**
 * The comparator behind each sort. Defined here rather than in the endpoint or the card, because
 * the API pages its rows with it and the card reorders the page it was given: two different
 * orders would make "the first 200" mean two different things.
 *
 * `start` and `end` run down the calendar, ties by hours then name; `hours` is the biggest work
 * first; `name` is alphabetical by client then name, which is how a planner scans for one.
 *
 * A row that is only in the compared run (Phase 27: work this run no longer puts in these weeks)
 * has no bar of its own, so it takes its date from its ghost. Sorting it to the top of the
 * calendar, where an empty string would put it, would give the dates of a run nobody asked about
 * the loudest position on the screen.
 * @param {'start' | 'end' | 'hours' | 'name'} by
 */
export function timelineComparator(by = 'start') {
  const name = (r) => `${r.client ?? ''} ${r.name ?? ''}`.trim().toLowerCase();
  const start = (r) => String(r.bar_start ?? r.ghost?.bar_start ?? '');
  const end = (r) => String(r.bar_end ?? r.ghost?.bar_end ?? '');
  const byName = (a, b) => name(a).localeCompare(name(b));
  const byHours = (a, b) => (b.hours ?? 0) - (a.hours ?? 0) || byName(a, b);
  if (by === 'hours') return byHours;
  if (by === 'name') return (a, b) => byName(a, b) || byHours(a, b);
  if (by === 'end') return (a, b) => end(b).localeCompare(end(a)) || byHours(a, b);
  return (a, b) => start(a).localeCompare(start(b)) || byHours(a, b);
}

/** Order rows without mutating the input. An unknown sort falls back to `start`. */
export function sortTimelineRows(rows, by = 'start') {
  return [...(rows ?? [])].sort(timelineComparator(TIMELINE_SORTS.includes(by) ? by : 'start'));
}

// ---------------------------------------------------------------------------------------------
// Geometry

/**
 * Where a bar sits across the drawn weeks.
 *
 * A bar is placed on whole week columns, because the allocation rows it is drawn from are weekly
 * (TRD 8.2): a project whose window starts on a Wednesday occupies that whole column, and saying
 * otherwise would invent a precision the forecast does not have. The exact dates are on the row
 * and in the tooltip.
 *
 * Work that runs past either edge of the window is clipped to it and flagged, never dropped and
 * never drawn outside the plot: `continues_before` and `continues_after` are what the card draws
 * the arrow heads from.
 *
 * `x` and `width` are percentages of the plot, so the same geometry serves any width without a
 * measured pixel anywhere.
 *
 * @param {{ bar_start: string, bar_end: string }} row
 * @param {{ week_start: string, week_end: string }[]} weeks
 */
export function barGeometry(row, weeks) {
  const count = weeks?.length ?? 0;
  if (!count || !row?.bar_start || !row?.bar_end) return null;
  const first = weeks[0];
  const last = weeks[count - 1];
  if (row.bar_end < first.week_start || row.bar_start > last.week_end) return null;

  let startIndex = weeks.findIndex((w) => row.bar_start <= w.week_end);
  if (startIndex < 0) startIndex = count - 1;
  let endIndex = count - 1;
  for (let i = count - 1; i >= 0; i -= 1) {
    if (row.bar_end >= weeks[i].week_start) {
      endIndex = i;
      break;
    }
  }
  if (endIndex < startIndex) endIndex = startIndex;
  const span = endIndex - startIndex + 1;
  return {
    start_index: startIndex,
    end_index: endIndex,
    span,
    continues_before: row.bar_start < first.week_start,
    continues_after: row.bar_end > last.week_end,
    x: round2((startIndex / count) * 100),
    width: round2((span / count) * 100),
  };
}

/** The left edge of week `index`, as a percentage of the plot. */
export function weekX(index, count) {
  return count > 0 ? round2((index / count) * 100) : 0;
}

/** The width of one week column, as a percentage of the plot. */
export function weekWidth(count) {
  return count > 0 ? round2(100 / count) : 100;
}

/**
 * Whole weeks between where a bar was planned to start and where it starts now — the `+2w` the
 * PRD sketch writes beside a slipped bar. Null when nothing slipped.
 *
 * Phase 9 records `slipped_from` as the earliest *planned* start among the re-anchored
 * assignments, so this is a statement about the plan the run read, not about a previous run.
 * The comparison between two runs is the ghost's `moved_weeks` (Phase 27), which is a different
 * number for a different question: this one asks how far the work is from where it was planned,
 * that one how far it is from where we last said it would be.
 */
export function slipWeeks(row) {
  if (!row?.slipped || !row.slipped_from || !row.bar_start) return null;
  const from = Date.parse(`${row.slipped_from}T00:00:00Z`);
  const to = Date.parse(`${row.bar_start}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  return Math.round((to - from) / (7 * 24 * 3600 * 1000));
}

/**
 * The weeks of a bar that carry no hours: a bar with a hole in it, which happens when a project's
 * assignments leave a gap in the middle of its window. The card draws those columns hollow rather
 * than claiming continuous work.
 * @param {{ weekly_hours: number[] }} row
 * @param {ReturnType<typeof barGeometry>} geometry
 */
export function barGaps(row, geometry) {
  if (!geometry || !Array.isArray(row?.weekly_hours)) return [];
  const gaps = [];
  for (let i = geometry.start_index; i <= geometry.end_index; i += 1)
    if (!(row.weekly_hours[i] > 0)) gaps.push(i);
  return gaps;
}

// ---------------------------------------------------------------------------------------------
// Comparison with another run (Phase 27, V-15: the ghost bars)

/**
 * What happened to one bar between the compared run and this one.
 *
 * Both runs are asked the same question — which weeks did you put this entity's hours in — over
 * the same week columns, so the five answers are exhaustive. They are ordered by how much they
 * cost a planner to read: `moved` is the one the view exists for.
 *
 * `new` and `gone` are about *these weeks*, not about the record: a project whose work has been
 * pushed past the end of the window reads as `gone` here and is still in the forecast, which is
 * why the ghost carries `elsewhere_in_run` and the card says so rather than implying the work
 * was cancelled.
 */
export const COMPARISONS = Object.freeze([
  Object.freeze({
    key: 'moved',
    label: 'Moved',
    hint: 'The hours are in different weeks than the compared run put them in',
  }),
  Object.freeze({
    key: 'changed',
    label: 'Changed',
    hint: 'The same weeks, a different number of hours',
  }),
  Object.freeze({
    key: 'new',
    label: 'New',
    hint: 'The compared run put no hours for this work in these weeks',
  }),
  Object.freeze({
    key: 'gone',
    label: 'Gone',
    hint: 'The compared run put hours in these weeks and this one puts none',
  }),
  Object.freeze({
    key: 'unchanged',
    label: 'Unchanged',
    hint: 'The same weeks and the same hours in both runs',
  }),
]);

export const COMPARISON_KEYS = Object.freeze(COMPARISONS.map((c) => c.key));

const COMPARISON_BY_KEY = new Map(COMPARISONS.map((c) => [c.key, c]));

/** What a comparison is called, for a tooltip, a legend or a table cell. */
export function comparisonOf(key) {
  return COMPARISON_BY_KEY.get(key) ?? null;
}

/** Hours within this of each other are the same hours: two runs of one plan round alike. */
export const HOURS_EPSILON = 0.01;

const MS_WEEK = 7 * 24 * 3600 * 1000;

/**
 * Whole weeks from `from` to `to`, signed: positive where `to` is the later date. Null unless
 * both dates are readable. Both ends are week starts, because a bar is placed on whole week
 * columns, so this counts columns rather than approximating them.
 */
export function weekDelta(from, to) {
  if (!from || !to) return null;
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / MS_WEEK);
}

/**
 * Which of the five words describes this pair. Dates first: a bar that is in different weeks has
 * moved, whatever its hours did, because that is the reading the view is for. Hours only decide
 * between `changed` and `unchanged`, and only past `HOURS_EPSILON`.
 * @param {{ bar_start?: string | null, bar_end?: string | null, hours?: number } | null} row
 * @param {{ bar_start?: string | null, bar_end?: string | null, hours?: number } | null} ghost
 */
export function classifyComparison(row, ghost) {
  const here = row?.bar_start ? row : null;
  if (!ghost?.bar_start) return here ? 'new' : 'unchanged';
  if (!here) return 'gone';
  if (here.bar_start !== ghost.bar_start || here.bar_end !== ghost.bar_end) return 'moved';
  if (Math.abs((here.hours ?? 0) - (ghost.hours ?? 0)) > HOURS_EPSILON) return 'changed';
  return 'unchanged';
}

/**
 * The movement between the two runs: the weeks the bar's start and end moved, and the hours
 * gained or lost over the drawn window. Signed, because work moves earlier as well as later —
 * a finished task releases its remaining plan and the bar in front of it comes back.
 */
export function ghostDeltas(row, ghost) {
  if (!ghost) return { start_weeks: null, end_weeks: null, hours: null };
  return {
    start_weeks: weekDelta(ghost.bar_start, row?.bar_start ?? null),
    end_weeks: weekDelta(ghost.bar_end, row?.bar_end ?? null),
    hours: round2((row?.hours ?? 0) - (ghost.hours ?? 0)),
  };
}

/**
 * One run's bar joined to another's: the ghost as the response carries it, with the movement
 * already worked out, and the word for what happened.
 *
 * Shared by the endpoint and the card on purpose. The endpoint pages and sorts with these
 * figures and puts them in the table and the CSV; the card draws them. Computing them twice is
 * how the export and the picture come to disagree.
 */
export function compareBars(row, ghost) {
  const comparison = classifyComparison(row, ghost);
  if (!ghost) return { comparison, ghost: null };
  const deltas = ghostDeltas(row, ghost);
  return {
    comparison,
    ghost: {
      ...ghost,
      moved_weeks: deltas.start_weeks,
      end_moved_weeks: deltas.end_weeks,
      hours_delta: deltas.hours,
    },
  };
}

/**
 * The label beside a compared bar: `+3w` where the work moved later, `−2w` where it moved
 * earlier (a real minus sign, not a hyphen), and the word itself for work that arrived or left.
 *
 * Null where nothing moved. A label on every bar would be noise, and for the same reason the
 * outline under an unmoved bar is not drawn either: two marks in one place saying one thing.
 */
export function comparisonLabel(row) {
  if (!row?.comparison || row.comparison === 'unchanged') return null;
  if (row.comparison === 'new') return 'new';
  if (row.comparison === 'gone') return 'gone';
  const weeks = row.ghost?.moved_weeks ?? null;
  if (!weeks) return null;
  return weeks > 0 ? `+${weeks}w` : `−${Math.abs(weeks)}w`;
}

// ---------------------------------------------------------------------------------------------
// Model

/**
 * Everything the card draws: the week columns, the capacity lane, and one drawable row per bar
 * with its geometry, its fill, its type hue and its status border already resolved.
 *
 * The palette is the whole portfolio's assignment from `GET /app/bootstrap` (PRD 7.3: the hue a
 * type wears never moves because a filter removed something), so the card passes it in rather
 * than deriving one from the rows on screen.
 *
 * @param {object} data  a `GET /forecast/timeline` response
 * @param {{ sort?: string, kinds?: string[], palette?: object[] }} [opts]
 */
export function timelineModel(data, { sort = 'start', kinds = TIMELINE_KINDS, palette = [] } = {}) {
  const weeks = (data?.weeks ?? []).map((w, index) => ({ ...w, index }));
  if (!weeks.length) return null;
  const wanted = new Set(kinds?.length ? kinds : TIMELINE_KINDS);

  const rows = sortTimelineRows(
    (data.rows ?? []).filter((r) => wanted.has(r.kind)),
    sort,
  )
    .map((row) => {
      const geometry = barGeometry(row, weeks);
      const ghost = row.ghost ? { ...row.ghost, geometry: barGeometry(row.ghost, weeks) } : null;
      // A row is drawable if either run put it on these columns: without the second clause the
      // work that left the window would vanish from the view whose job is to notice it.
      if (!geometry && !ghost?.geometry) return null;
      const slot = typeSlot(palette, row.canonical_type);
      const status = statusOf(row.kind);
      return {
        ...row,
        geometry,
        ghost,
        comparison: row.comparison ?? null,
        comparison_label: comparisonLabel(row),
        // Only a bar that moved, or one that has no bar left, has an outline worth drawing:
        // an outline traced under an unchanged bar is a second mark saying the first one's thing.
        show_ghost: Boolean(ghost?.geometry) && ['moved', 'gone'].includes(row.comparison),
        gaps: barGaps(row, geometry),
        fill: row.fill ?? fillForTier(row.confidence_tier),
        slot,
        status,
        slip_weeks: slipWeeks(row),
        peak_index: row.peak_week ? weeks.findIndex((w) => w.week_start === row.peak_week) : -1,
      };
    })
    .filter(Boolean);

  return {
    weeks,
    lane: data.lane ?? [],
    rows,
    thresholds: data.thresholds ?? {
      underload_below: 0.85,
      caution_above: 1,
      overloaded_above: 1.15,
    },
    sort,
    kinds: [...wanted],
    dimension: data.dimension ?? 'company',
    entity: data.entity ?? null,
    compare: data.compare ?? null,
  };
}

/** The figures the card's header states. Counted over the rows actually drawn, not the response. */
export function timelineSummary(model) {
  const perKind = Object.fromEntries(TIMELINE_KINDS.map((k) => [k, 0]));
  const perTier = { high: 0, medium: 0, low: 0 };
  const perComparison = Object.fromEntries(COMPARISON_KEYS.map((k) => [k, 0]));
  let hours = 0;
  let hoursThen = 0;
  let slipped = 0;
  let inferred = 0;
  let continues = 0;
  let movedLater = 0;
  let movedEarlier = 0;
  let movedEndOnly = 0;
  for (const row of model?.rows ?? []) {
    perKind[row.kind] = (perKind[row.kind] ?? 0) + 1;
    if (row.confidence_tier) perTier[row.confidence_tier] += 1;
    hours = round2(hours + (row.hours ?? 0));
    if (row.slipped) slipped += 1;
    if (row.schedule_source === 'inferred' || row.source_mode === 'inferred_role_mix')
      inferred += 1;
    if (row.geometry?.continues_after) continues += 1;
    if (row.comparison) perComparison[row.comparison] += 1;
    if (row.ghost) {
      hoursThen = round2(hoursThen + (row.ghost.hours ?? 0));
      if (row.ghost.moved_weeks > 0) movedLater += 1;
      if (row.ghost.moved_weeks < 0) movedEarlier += 1;
      // A bar whose start held and whose end did not: on different columns, and with no move
      // in weeks to label it with. Counted apart so the figures still add up to `moved`.
      if (row.comparison === 'moved' && !row.ghost.moved_weeks) movedEndOnly += 1;
    }
  }
  const compared = Boolean(model?.compare);
  const lane = model?.lane ?? [];
  const peak = lane.reduce(
    (best, cell) =>
      cell.utilization !== null && (best === null || cell.utilization > best.utilization)
        ? cell
        : best,
    null,
  );
  return {
    rows: model?.rows?.length ?? 0,
    per_kind: perKind,
    per_tier: perTier,
    hours,
    slipped,
    inferred,
    continues_after: continues,
    weeks: model?.weeks?.length ?? 0,
    weeks_over: lane.filter((c) => c.headroom_hours < 0).length,
    peak,
    // Null rather than a row of zeros when no run is being compared: "nothing moved" and "you
    // did not ask" are different statements, and the card says different things about them.
    comparison: compared
      ? {
          run_id: model.compare.run_id ?? null,
          planning_date: model.compare.planning_date ?? null,
          per_comparison: perComparison,
          moved_later: movedLater,
          moved_earlier: movedEarlier,
          moved_end_only: movedEndOnly,
          hours_then: hoursThen,
          hours_delta: round2(hours - hoursThen),
        }
      : null,
  };
}

/**
 * The table view's rows: one per bar, the dates and hours the bar is drawn from, so the gate's
 * comparison with Workamajig can be done from the export rather than from a tooltip.
 */
export function timelineExportRows(model) {
  return (model?.rows ?? []).map((row) => ({
    kind: KIND_BY_KEY.get(row.kind)?.label ?? row.kind,
    name: row.name,
    client: row.client,
    number: row.number ?? row.source_id,
    canonical_type: row.canonical_type,
    confidence_tier: row.confidence_tier,
    fill: row.fill,
    source_mode: row.source_mode,
    schedule_source: row.schedule_source,
    window_start: row.window_start,
    window_end: row.window_end,
    bar_start: row.bar_start,
    bar_end: row.bar_end,
    slipped_from: row.slipped_from,
    slip_weeks: row.slip_weeks,
    hours: row.hours,
    hours_horizon: row.hours_horizon,
    high_confidence_hours: row.high_confidence_hours,
    peak_week: row.peak_week,
    peak_hours: row.peak_hours,
    weeks_with_hours: row.weeks_with_hours,
    continues_after: row.geometry?.continues_after ?? false,
    // The comparison columns (Phase 27). Always written, null when no run is being compared:
    // the card decides which columns a reader sees, and the row shape stays one shape.
    comparison: row.comparison ?? null,
    ghost_bar_start: row.ghost?.bar_start ?? null,
    ghost_bar_end: row.ghost?.bar_end ?? null,
    moved_weeks: row.ghost?.moved_weeks ?? null,
    end_moved_weeks: row.ghost?.end_moved_weeks ?? null,
    hours_then: row.ghost?.hours ?? null,
    hours_delta: row.ghost?.hours_delta ?? null,
  }));
}

/** The lane's own export: the capacity row the bars are read against, week by week. */
export function laneExportRows(model) {
  return (model?.lane ?? []).map((cell) => ({
    week_start: cell.week_start,
    allocated_hours: cell.allocated_hours,
    capacity_hours: cell.capacity_hours,
    headroom_hours: cell.headroom_hours,
    utilization: cell.utilization,
    band: cell.band,
    projects: cell.projects,
    opportunities: cell.opportunities,
  }));
}

/** Convenience for the card and the tests: the numbers of one week of one row. */
export function weekOf(row, index) {
  const hours = Array.isArray(row?.weekly_hours) ? (row.weekly_hours[index] ?? 0) : 0;
  return { index, hours: num(hours) ?? 0 };
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// A specification, not a rendering: the screen decorates it with `withFormats` and the CSV
// writer reads the same list, so an export cannot hold a different set of columns from the
// table it came from.

/**
 * The comparison columns exist on every row (the model writes them null), but a reader who asked
 * for no comparison should not have six empty columns to scroll past, so they are added only
 * when one was asked for.
 * @param {{ comparing?: boolean }} opts
 */
export function timelineColumns({ comparing = false } = {}) {
  const date = (key, label) => col.date(key, label, { unit: 'week', csv: (v) => v ?? '' });
  return [
    col.text('kind', 'Kind'),
    col.text('name', 'Name'),
    col.text('client', 'Client'),
    col.text('number', 'Number'),
    col.text('canonical_type', 'Type', { csv: (v) => v ?? '' }),
    col.text('confidence_tier', 'Confidence', { csv: (v) => v ?? '' }),
    col.text('schedule_source', 'Window from', { csv: (v) => v ?? '' }),
    date('window_start', 'Planned start'),
    date('window_end', 'Planned end'),
    date('bar_start', 'Bar start'),
    date('bar_end', 'Bar end'),
    date('slipped_from', 'Slipped from'),
    col.number('slip_weeks', 'Slip (weeks)', { csv: (v) => v ?? '' }),
    col.hours('hours', 'Hours in window'),
    col.hours('high_confidence_hours', 'High-confidence hours'),
    date('peak_week', 'Peak week'),
    col.hours('peak_hours', 'Peak hours'),
    ...(comparing
      ? [
          col.text('comparison', 'Since compared run', { csv: (v) => v ?? '' }),
          date('ghost_bar_start', 'Was start'),
          date('ghost_bar_end', 'Was end'),
          col.number('moved_weeks', 'Start moved (weeks)'),
          col.number('end_moved_weeks', 'End moved (weeks)'),
          col.hours('hours_then', 'Hours then'),
          col.hours('hours_delta', 'Hours change'),
        ]
      : []),
  ];
}
